import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { proyeccionesAPI, scoringAPI } from '../services/api';

const fmt = (v) => (v === null || v === undefined ? '—' : typeof v === 'number' ? v.toLocaleString('es-AR') : v);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const ProyeccionesPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cohorteH, setCohorteH] = useState(null);
  const [cohorteM, setCohorteM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('prenez');

  useEffect(() => {
    if (user?.establecimiento_id) loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projRes, cohHRes, cohMRes] = await Promise.allSettled([
        proyeccionesAPI.getCompleto(user.establecimiento_id),
        scoringAPI.evalCohorte(user.establecimiento_id, 'hembra', 10, 60),
        scoringAPI.evalCohorte(user.establecimiento_id, 'macho', 6, 30),
      ]);
      if (projRes.status === 'fulfilled') setData(projRes.value.data);
      if (cohHRes.status === 'fulfilled') setCohorteH(cohHRes.value.data);
      if (cohMRes.status === 'fulfilled') setCohorteM(cohMRes.value.data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'prenez', label: 'Prenez' },
    { id: 'paricion', label: 'Paricion' },
    { id: 'produccion', label: 'Produccion' },
    { id: 'descartes', label: 'Descartes' },
    { id: 'ranking', label: 'Ranking Vientres' },
    { id: 'scoring', label: 'Scoring Cohorte' },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="proyecciones-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Proyecciones Predictivas</h2>
          <p className="text-sm text-gray-500 mt-1">Temporada {data?.preñez?.temporada || new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto bg-gray-100 rounded-lg p-1 gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`proy-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* === PRENEZ === */}
      {activeTab === 'prenez' && data?.preñez && (
        <div className="space-y-6" data-testid="proy-prenez-content">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-emerald-800 mb-2">Prenez Proyectada</p>
              <p className="text-5xl font-bold text-emerald-700">{data.preñez.preñez_proyectada}</p>
              <p className="text-sm text-emerald-600 mt-2">cabezas</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-gray-700 mb-2">Vacas Aptas</p>
              <p className="text-5xl font-bold text-gray-800">{data.preñez.vacas_aptas}</p>
              <p className="text-sm text-gray-500 mt-2">disponibles para servicio</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-blue-800 mb-2">Tasa Prenez Historica</p>
              <p className="text-5xl font-bold text-blue-700">{fmtPct(data.preñez.tasa_preñez_historica)}</p>
              <p className="text-sm text-blue-600 mt-2">ultimos 3 anos</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-3">Detalle de calculo</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500">Temporada:</span> <strong>{data.preñez.temporada}</strong></div>
              <div><span className="text-gray-500">Vacas aptas:</span> <strong>{data.preñez.vacas_aptas}</strong></div>
              <div><span className="text-gray-500">Tasa hist.:</span> <strong>{fmtPct(data.preñez.tasa_preñez_historica)}</strong></div>
              <div><span className="text-gray-500">Proyectada:</span> <strong>{data.preñez.preñez_proyectada}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* === PARICION === */}
      {activeTab === 'paricion' && data?.paricion && (
        <div className="space-y-6" data-testid="proy-paricion-content">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-purple-800 mb-2">Terneros Proyectados</p>
              <p className="text-5xl font-bold text-purple-700">{data.paricion.terneros_proyectados}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-blue-800 mb-2">Machos Estimados</p>
              <p className="text-5xl font-bold text-blue-700">{data.paricion.machos_estimados}</p>
            </div>
            <div className="bg-pink-50 border border-pink-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-pink-800 mb-2">Hembras Estimadas</p>
              <p className="text-5xl font-bold text-pink-700">{data.paricion.hembras_estimadas}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-3">Parametros</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-gray-500">Vacas prenadas:</span> <strong>{data.paricion.vacas_preñadas}</strong></div>
              <div><span className="text-gray-500">Tasa paricion hist.:</span> <strong>{fmtPct(data.paricion.tasa_paricion_historica)}</strong></div>
              <div><span className="text-gray-500">Ratio M/H:</span> <strong>{data.paricion.ratio_machos}/{data.paricion.ratio_hembras}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* === PRODUCCION === */}
      {activeTab === 'produccion' && data?.produccion && (
        <div className="space-y-6" data-testid="proy-produccion-content">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <p className="text-sm font-semibold text-green-800 mb-2">Kg Totales Proyectados</p>
              <p className="text-5xl font-bold text-green-700">{fmt(data.produccion.kg_totales_proyectados)}</p>
              <p className="text-sm text-green-600 mt-2">kg al destete</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <p className="text-sm font-semibold text-amber-800 mb-3">Pesos Promedio por Cabeza</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Machos:</span>
                  <span className="text-lg font-bold text-blue-700">{data.produccion.peso_promedio_macho} kg</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Hembras:</span>
                  <span className="text-lg font-bold text-pink-700">{data.produccion.peso_promedio_hembra} kg</span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-sm text-gray-600">Terneros proyectados:</span>
                  <span className="text-lg font-bold">{data.produccion.terneros_proyectados}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === DESCARTES === */}
      {activeTab === 'descartes' && data?.descartes && (
        <div className="space-y-6" data-testid="proy-descartes-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-5 text-center"><p className="text-3xl font-bold">{data.descartes.total_vacas}</p><p className="text-xs text-gray-500 mt-1">Total Vacas</p></div>
            <div className="bg-red-50 rounded-xl p-5 text-center"><p className="text-3xl font-bold text-red-600">{data.descartes.alto_riesgo}</p><p className="text-xs text-gray-500 mt-1">Alto Riesgo</p></div>
            <div className="bg-yellow-50 rounded-xl p-5 text-center"><p className="text-3xl font-bold text-yellow-600">{data.descartes.medio_riesgo}</p><p className="text-xs text-gray-500 mt-1">Medio Riesgo</p></div>
            <div className="bg-green-50 rounded-xl p-5 text-center"><p className="text-3xl font-bold text-green-600">{data.descartes.bajo_riesgo}</p><p className="text-xs text-gray-500 mt-1">Bajo Riesgo</p></div>
          </div>

          {data.descartes.detalle?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-4">Detalle de Animales en Riesgo ({data.descartes.vacas_en_riesgo})</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">VID</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Categoria</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Edad</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Motivos</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Riesgo</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Auto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.descartes.detalle.map((a, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-semibold">{a.VID}</td>
                        <td className="py-2 px-3 text-gray-600">{a.categoria}</td>
                        <td className="py-2 px-3 text-gray-600">{a.edad_años}a</td>
                        <td className="py-2 px-3 text-xs text-gray-600 max-w-xs truncate">{a.motivos?.join(', ')}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${a.nivel_riesgo > 70 ? 'bg-red-100 text-red-800' : a.nivel_riesgo > 40 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {a.nivel_riesgo}%
                          </span>
                        </td>
                        <td className="py-2 px-3">{a.automatico ? <span className="px-2 py-0.5 bg-red-500 text-white rounded text-xs font-bold">AUTO</span> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === RANKING VIENTRES === */}
      {activeTab === 'ranking' && data?.ranking_top10?.ranking?.length > 0 && (
        <div className="space-y-6" data-testid="proy-ranking-content">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4">Ranking de Vientres por Eficiencia Productiva</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    {['#','VID','Edad','EP Prom.','Terneros','Kg Acum.','Prenada','Score','Categoria'].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.ranking_top10.ranking.map((v) => (
                    <tr key={v.posicion} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 font-bold text-gray-800">{v.posicion}</td>
                      <td className="py-2 px-3 font-semibold">{v.VID}</td>
                      <td className="py-2 px-3 text-gray-600">{v.edad_años}a</td>
                      <td className="py-2 px-3">{v.ep_promedio}%</td>
                      <td className="py-2 px-3">{v.total_terneros}</td>
                      <td className="py-2 px-3">{v.kg_acumulados} kg</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${v.preñada ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{v.preñada ? 'Si' : 'No'}</span>
                      </td>
                      <td className="py-2 px-3 font-bold text-lg text-green-700">{v.score}</td>
                      <td className="py-2 px-3 text-xs text-gray-500">{v.categoria_ranking}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === SCORING COHORTE === */}
      {activeTab === 'scoring' && (
        <div className="space-y-6" data-testid="proy-scoring-content">
          {/* Hembras */}
          {cohorteH && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-1">Scoring Hembras ({cohorteH.rango_edad})</h3>
              <p className="text-sm text-gray-500 mb-4">{cohorteH.total} animales evaluadas — Puntajes 1-10 entre contemporaneas</p>
              {cohorteH.resultados?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {['Rank','VID','Edad','Madurez','P/A','T.Crec','IEP','Sumatoria','Promedio'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohorteH.resultados.slice(0, 20).map((r) => (
                        <tr key={r.animal_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-bold">{r.ranking}</td>
                          <td className="py-2 px-3 font-semibold">{r.VID}</td>
                          <td className="py-2 px-3 text-gray-600">{r.edad_meses}m</td>
                          <td className="py-2 px-3">{r.scores?.madurez_fisiologica?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.relacion_peso_altura?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.tasa_crecimiento?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.iep?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3 font-bold text-green-700">{r.sumatoria_puntajes}</td>
                          <td className="py-2 px-3">{r.promedio_puntajes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-gray-500 text-sm">Sin datos de scoring para este rango de edad.</p>}
            </div>
          )}

          {/* Machos */}
          {cohorteM && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-base font-bold text-gray-900 mb-1">Scoring Machos ({cohorteM.rango_edad})</h3>
              <p className="text-sm text-gray-500 mb-4">{cohorteM.total} animales evaluados</p>
              {cohorteM.resultados?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {['Rank','VID','Edad','Madurez','P/A','T.Crec','CE Rel','Sumatoria','Promedio'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cohorteM.resultados.slice(0, 20).map((r) => (
                        <tr key={r.animal_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-bold">{r.ranking}</td>
                          <td className="py-2 px-3 font-semibold">{r.VID}</td>
                          <td className="py-2 px-3 text-gray-600">{r.edad_meses}m</td>
                          <td className="py-2 px-3">{r.scores?.madurez_fisiologica?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.relacion_peso_altura?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.tasa_crecimiento?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3">{r.scores?.ce_relativa?.puntaje ?? '—'}</td>
                          <td className="py-2 px-3 font-bold text-green-700">{r.sumatoria_puntajes}</td>
                          <td className="py-2 px-3">{r.promedio_puntajes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-gray-500 text-sm">Sin datos de scoring para este rango de edad.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProyeccionesPage;
